import { isEqual, isPlainObject, mapValues, omitBy } from 'lodash';

export type ChangeMark = 'new' | 'edited' | 'removed';

export interface ListChanges<T> {
    /** Every changed item, keyed by id: new, edited, or removed (missing from the draft). */
    marks: Record<string, ChangeMark>;
    /** Baseline items missing from the draft, so callers can keep showing them as removed. */
    removed: T[];
    /** Total pending changes: one per mark. */
    count: number;
}

// Drops undefined and '' fields at any depth: a rebuilt form value carries them where the stored object has no key.
const pruneEmpty = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(pruneEmpty);
    if (isPlainObject(value)) {
        return omitBy(mapValues(value as object, pruneEmpty), (field) => field === undefined || field === '');
    }
    return value;
};

/** Deep equality that ignores key order and empty values, so rebuilt objects match their baseline. */
export const sameValue = (a: unknown, b: unknown) => isEqual(pruneEmpty(a), pruneEmpty(b));

export const diffList = <T>(draft: T[], baseline: T[], getId: (item: T) => string): ListChanges<T> => {
    const baselineById = new Map(baseline.map((item) => [getId(item), item]));
    const marks: Record<string, ChangeMark> = {};
    for (const item of draft) {
        const original = baselineById.get(getId(item));
        if (!original) marks[getId(item)] = 'new';
        else if (!sameValue(item, original)) marks[getId(item)] = 'edited';
    }
    const draftIds = new Set(draft.map(getId));
    const removed = baseline.filter((item) => !draftIds.has(getId(item)));
    for (const item of removed) marks[getId(item)] = 'removed';
    return { marks, removed, count: Object.keys(marks).length };
};
